import type { ProvenanceDocumentKind, ProvenanceEvidenceClaim } from "@ondo/shared";

import { HistoryFields } from "./HistoryFields.tsx";
import { ProvenanceDocuments } from "./ProvenanceDocuments.tsx";
import { ProvenanceEntryRemoval } from "./ProvenanceEntryRemoval.tsx";
import { ProvenanceVerificationPanel } from "./ProvenanceVerification.tsx";
import {
  EVIDENCE_GRADES,
  PROVENANCE_COPY,
  PROVENANCE_LABELS,
} from "./provenance.constants.ts";
import type {
  DraftEntry,
  EntryFormModel,
  VerificationIntent,
} from "./provenance.model.ts";

/**
 * The editable expansion. Reachable only from a draft.
 *
 * There is no counterpart for a recorded entry, and that is the append-only rule
 * made structural rather than guarded: an entry the registry holds has no form
 * to render, so no handler here can be pointed at one.
 *
 * The fields themselves are `HistoryFields`, which reads them off the draft's
 * own kind — so this component is the frame around a form rather than a form,
 * and the modal on the work record puts the same fields in a different frame.
 */
export function ProvenanceEntryForm({
  base,
  form,
  refusals,
  onSetField,
  onAddDocuments,
  onSetDocumentKind,
  onOpenDocument,
  onRemoveDocument,
  onAttachOndoId,
  onDetachOndoId,
  onRemove,
  onAct,
  onUnact,
}: {
  /** One useId per row, so every field id in it is unique and stable. */
  base: string;
  form: EntryFormModel;
  refusals: string[];
  onSetField: <K extends keyof DraftEntry>(key: K, value: DraftEntry[K]) => void;
  onAddDocuments: (files: File[]) => void;
  onSetDocumentKind: (documentId: string, kind: ProvenanceDocumentKind) => void;
  onOpenDocument: (documentId: string) => void;
  onRemoveDocument: (documentId: string) => void;
  onAttachOndoId: (raw: string) => string | null;
  onDetachOndoId: () => void;
  onRemove: () => void;
  onAct: (intent: VerificationIntent) => string | null;
  onUnact: (actId: string) => void;
}) {
  return (
    <div className="provenance__body">
      <HistoryFields
        base={base}
        values={form.values}
        ondo={form.ondo}
        onSetField={onSetField}
        onAttachOndoId={onAttachOndoId}
        onDetachOndoId={onDetachOndoId}
      />

      <div className="provenance__field">
        <label htmlFor={`${base}-grade`}>{PROVENANCE_LABELS.grade}</label>
        <select
          id={`${base}-grade`}
          value={form.values.grade}
          onChange={(event) =>
            onSetField("grade", event.target.value as ProvenanceEvidenceClaim)
          }
        >
          {EVIDENCE_GRADES.map((grade) => (
            <option key={grade.key} value={grade.key}>
              {grade.label}
            </option>
          ))}
        </select>
        {/* Said here rather than only in a tooltip: this is the one field that
            is entirely the submitter's assertion, and the pill it produces reads
            like a finding unless somebody says otherwise. */}
        <p className="provenance__help">{PROVENANCE_COPY.gradeHelp}</p>
      </div>

      <ProvenanceDocuments
        rows={form.documents}
        refusals={refusals}
        editable
        onAdd={onAddDocuments}
        onSetKind={onSetDocumentKind}
        onOpen={onOpenDocument}
        onRemove={onRemoveDocument}
        // Unreachable, and deliberately so: every document on this surface is a
        // file on the device, and `documentVerdictFor` is never asked about one
        // — a draft's row carries `verdict: null`, so `DocumentVerdict` does
        // not render and neither of these can be called. Answering with a real
        // handler would be a lie about a screen that cannot show the control.
        onJudge={() => null}
        onUndoVerdict={() => undefined}
      />

      <ProvenanceVerificationPanel
        panel={form.verification}
        onAct={onAct}
        onUnact={onUnact}
      />

      <ProvenanceEntryRemoval removal={form.removal} onRemove={onRemove} />
    </div>
  );
}
